import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { Button, CircularProgress, IconButton } from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import axios from "axios";
import InputField from "components/Input/InputField";
import { API_URL } from "const/constants";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { isUserOrganizationDeleted } from "utils/organizationAccess";
import { currentUserHomeRoute } from "utils/referralRouting";

export default function FirstLoginSetPassword() {
  const navigate = useNavigate();
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const mismatch = confirmPassword.length > 0 && newPassword !== confirmPassword;

  useEffect(() => {
    if (isUserOrganizationDeleted()) {
      navigate("/organization-deleted");
    }
  }, [navigate]);

  const { isLoading, mutate } = useMutation(
    async () => {
      const res = await axios.put(
        `${API_URL}/auth/set-password`,
        { newPassword },
        { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
      );
      return res.data;
    },
    {
      onSuccess() {
        toast.success("Password updated successfully!");
        if (isUserOrganizationDeleted()) {
          navigate("/organization-deleted");
          return;
        }
        navigate(currentUserHomeRoute());
      },
      onError(err) {
        toast.error(
          err?.response?.data?.errorMessage ||
            err?.response?.data?.message ||
            "Failed to update password. Please try again."
        );
      },
    }
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!newPassword || mismatch) return;
    mutate();
  };

  const handleSkip = () => {
    toast.info("You can change your password anytime from your profile.");
    if (isUserOrganizationDeleted()) {
      navigate("/organization-deleted");
      return;
    }
    navigate(currentUserHomeRoute());
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      <div className="mx-auto px-4 h-full w-full">
        <div className="flex content-center items-center justify-center h-full">
          <div className="w-full lg:w-3/12 px-4">
            <div className="relative flex flex-col min-w-0 break-words w-full mb-6 shadow-lg rounded-lg bg-app-surface-hover border-0">
              <div className="rounded-t mb-0 px-6 py-6">
                <div className="text-center mb-3">
                  <h3 className="text-app-text-muted text-xl font-bold uppercase">
                    Set New Password
                  </h3>
                  <p className="text-app-text-secondary text-sm mt-2">
                    Your account was set up with a temporary password. Set a new one now, or skip
                    and do it later.
                  </p>
                </div>

                <hr className="mt-6 border-b-1 border-app-border" />
              </div>

              <div className="flex-auto px-4 lg:px-10 py-10 pt-0">
                <form className="space-y-4 md:space-y-6" onSubmit={handleSubmit}>
                  <div className="relative w-full">
                    <InputField
                      label="New Password"
                      placeholder="Enter new password"
                      name="newPassword"
                      required
                      type={showNew ? "text" : "password"}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      endIcon={
                        <IconButton
                          size="small"
                          onClick={() => setShowNew((s) => !s)}
                          edge="end"
                          aria-label={showNew ? "Hide password" : "Show password"}
                        >
                          {showNew ? (
                            <VisibilityOff fontSize="small" />
                          ) : (
                            <Visibility fontSize="small" />
                          )}
                        </IconButton>
                      }
                    />
                  </div>

                  <div className="relative w-full">
                    <InputField
                      label="Confirm Password"
                      placeholder="Confirm new password"
                      name="confirmPassword"
                      required
                      type={showConfirm ? "text" : "password"}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      outerClassName={mismatch ? "border-rose-500" : ""}
                      endIcon={
                        <IconButton
                          size="small"
                          onClick={() => setShowConfirm((s) => !s)}
                          edge="end"
                          aria-label={showConfirm ? "Hide password" : "Show password"}
                        >
                          {showConfirm ? (
                            <VisibilityOff fontSize="small" />
                          ) : (
                            <Visibility fontSize="small" />
                          )}
                        </IconButton>
                      }
                    />
                    {mismatch && (
                      <p className="text-rose-500 text-xs mt-1 ml-1">Passwords do not match</p>
                    )}
                  </div>

                  <div className="text-center mt-6">
                    <Button
                      variant="contained"
                      className="w-full"
                      type="submit"
                      disabled={!newPassword || mismatch || isLoading}
                      startIcon={isLoading && <CircularProgress size={15} />}
                    >
                      {isLoading ? "Saving…" : "Set Password"}
                    </Button>
                  </div>
                </form>
              </div>
            </div>

            <div className="flex flex-wrap mt-6 relative">
              <div className="w-full text-center">
                <button type="button" onClick={handleSkip} className="text-slate-200">
                  <small>Skip for now</small>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
